import { Box, Modal, Popper, useMediaQuery } from '@mui/material';
import PresignedImg from 'Components/PresignedImg';
import CloseIcon from '@mui/icons-material/Close';
import RepertoireHamburger from '../../assets/images/Repertoire/RepertoireHamburger.png';
import RepertoireLeftArrow from '../../assets/images/Repertoire/RepertoireLeftArrow.png';
import RepertoireCheck from '../../assets/images/Repertoire/RepertoireCheck.png';
import { useState, useRef, useEffect, useMemo, MutableRefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as repertoireActions from '../../Actions/repertoire';
import { useSelector } from 'react-redux';
import { RepertoireData, RepertoireReducer, Composer, Genre } from 'Types/RepertoireTypes';
import { REPERTOIRE_PROGRESS } from 'Types/RepertoireTypes';
import { usePreventRepClick } from './DragScroll';
import { getNamesConcatted } from 'Utils';

type RepertoireItemProps = {
  repertoireData: RepertoireData;
  containerRef: MutableRefObject<HTMLDivElement | null>;
};

const RepertoireItem = (props: RepertoireItemProps) => {
  const preventRepClick = usePreventRepClick();
  const { repertoireData, containerRef } = props;
  const [showDetails, setShowDetails] = useState(false);
  const hamburgerRef = useRef<HTMLImageElement>(null);
  const songDetailsRef = useRef<HTMLDivElement>(null);

  const [showCompleteTooltip, setShowCompleteTooltip] = useState(false);
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const userRepertoireProgress = repertoireReducerData.userRepertoireProgresses;
  const selectUserRepertoireProgress = userRepertoireProgress?.find(
    (item) => item.repertoire?.id === repertoireData?.id,
  );
  // logger.info(repertoireData.id, selectUserRepertoireProgress)
  let selectRepertoireProgressPct = 0;
  if (selectUserRepertoireProgress && selectUserRepertoireProgress.percent_complete) {
    selectRepertoireProgressPct = selectUserRepertoireProgress.percent_complete;
  }

  let currentRepState;
  if (selectRepertoireProgressPct >= 100) {
    currentRepState = REPERTOIRE_PROGRESS.COMPLETE;
  } else if (selectRepertoireProgressPct > 0) {
    currentRepState = REPERTOIRE_PROGRESS.IN_PROGRESS;
  } else {
    currentRepState = REPERTOIRE_PROGRESS.NOT_STARTED;
  }

  const navigate = useNavigate();

  const getPosition = () => {
    if (hamburgerRef.current) {
      const rect = hamburgerRef.current.getBoundingClientRect();
      return { x: rect.x, y: rect.y };
    }
    return { x: 0, y: 0 };
  };

  const dispatch = useDispatch();

  const repertoireId = repertoireData.id;
  const title = repertoireData.name;
  let composer = '';
  repertoireData.composers.forEach((comp: Composer, idx: number) => {
    composer = composer.concat(idx > 0 ? `, ${comp.name}` : comp.name);
  });
  let genre = '';
  repertoireData.genres.forEach((gen: Genre, idx: number) => {
    genre = genre.concat(idx > 0 ? `, ${gen.name}` : gen.name);
  });
  const level = repertoireData.level?.level_number;
  const bpm = repertoireData.bpm;

  const previewSrc = repertoireData.repertoire_png_preview_url; //TestRepPreview1 //repertoireData.repertoire_png_preview_url

  const repertoireDetails: RepertoireDetails = {
    Name: title,
    Composer: composer,
    Level: level,
    Genre: genre,
    // Duration: '1:20',
    BPM: bpm,
  };

  let bodyColor;
  let levelColor;
  let borderColor;
  if (currentRepState == REPERTOIRE_PROGRESS.COMPLETE) {
    bodyColor = '#2C5950'; //'#469F6A'
    levelColor = '#2E5B3E';
    borderColor = '#469F6A';
  } else if (currentRepState == REPERTOIRE_PROGRESS.IN_PROGRESS) {
    bodyColor = '#2F416E'; //'#6F87F1'
    levelColor = '#31353F';
    borderColor = '#6F87F1';
  } else {
    bodyColor = '#07111D';
    levelColor = '#31353F';
    borderColor = '#767C8C';
  }

  useEffect(() => {
    // This function checks if the click is outside the modal
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node | null;
      if (songDetailsRef.current && target && !songDetailsRef.current.contains(target)) {
        setTimeout(() => {
          /// debounce so that you can click the tooltip three dots again to close
          setShowDetails(false);
        }, 150);
      }
    }
    // Add event listener when the component is mounted
    document.addEventListener('mousedown', handleClickOutside);
    // Remove event listener on cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [songDetailsRef, setShowDetails]); // Only re-run if detailsRef or setShowDetails changes

  return (
    <Box
      sx={{
        marginBottom: '20px',
        marginTop: '14px',
        minWidth: '160px',
        maxWidth: '160px',
        height: '180px',
        backgroundColor: bodyColor,
        borderRadius: '6px',
        border: `2px solid ${borderColor}`,
        position: 'relative',
        '&:hover': {
          cursor: showDetails ? 'unset' : 'pointer',
        },
      }}
      onClick={() => {
        if (!showDetails && !preventRepClick) {
          dispatch(repertoireActions.setRepertoireSelect({ repertoireIdSelect: repertoireId }));
          // Timeout because we want to set the state before navigating and using wrong repertoireId for anything
          // Not ideal, Same as the code in TierComplete.tsx
          setTimeout(() => {
            navigate('/repertoire-play');
          }, 150);
        }
      }}
    >
      {showCompleteTooltip && <CompleteToolTip pos={getPosition()} />}
      {showDetails && (
        <SongDetails
          repertoireDetails={repertoireDetails}
          setShowDetails={setShowDetails}
          songDetailsRef={songDetailsRef}
          containerRef={containerRef}
          anchorEl={hamburgerRef.current}
        />
      )}
      <Box
        sx={{
          width: '100%',
          height: '100px',
          backgroundColor: 'white',
          position: 'relative',
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          borderBottom: `2px solid ${borderColor}`,
        }}
      >
        <Box
          sx={{
            paddingTop: '0px',
            height: '100px',
            overflow: 'hidden',
            marginLeft: '0px',
            marginRight: '0px',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
          }}
        >
          <PresignedImg
            repertoireId={repertoireId}
            style={{ maxWidth: '94%' }}
            src={previewSrc}
            alt={title}
          />
        </Box>

        {currentRepState == REPERTOIRE_PROGRESS.IN_PROGRESS && (
          <Box
            sx={{
              position: 'absolute',
              left: '7px',
              top: '7px',
              backgroundColor: '#6F87F1',
              borderRadius: '10px',
              color: 'white',
              fontSize: '10px',
              padding: '4px 6px',
              fontWeight: '500',
            }}
            onMouseEnter={() => {
              setShowCompleteTooltip(true);
            }}
            onMouseLeave={() => {
              setShowCompleteTooltip(false);
            }}
          >
            {selectRepertoireProgressPct && Math.floor(selectRepertoireProgressPct)}% COMPLETE
          </Box>
        )}

        {currentRepState == REPERTOIRE_PROGRESS.COMPLETE && (
          <Box
            sx={{
              position: 'absolute',
              left: '6px',
              top: '6px',
            }}
          >
            <img style={{ width: '22px' }} src={RepertoireCheck} />
          </Box>
        )}

        <Box sx={{ ...levelStyles, ...{ backgroundColor: levelColor } }}>LVL {level || '--'}</Box>
        <Box
          sx={selectStyles}
          onClick={(e) => {
            e.stopPropagation();
            setShowDetails(!showDetails);
          }}
        >
          <img
            ref={hamburgerRef}
            style={{ marginTop: '0px', width: '15px' }}
            src={RepertoireHamburger}
          />
        </Box>
      </Box>

      <Box
        className="no-select"
        sx={{
          height: '74px',
          paddingBottom: '6px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          paddingLeft: '8px',
          paddingRight: '6px',
        }}
      >
        <Box sx={titleStyles}>{title}</Box>
        <Box sx={composerStyles}>
          {getNamesConcatted(
            repertoireData.artists.length ? repertoireData.artists : repertoireData.composers,
          )}
        </Box>
      </Box>
    </Box>
  );
};
export default RepertoireItem;

// eslint-disable-next-line react-refresh/only-export-components -- shared style constants
export const titleStyles = {
  marginTop: '5px',
  marginBottom: '7px',
  color: '#FFFFFF',
  fontFamily: 'Lato',
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '17px',
  letterSpacing: '0.01em',
  textAlign: 'left',
};

// eslint-disable-next-line react-refresh/only-export-components -- shared style constants
export const composerStyles = {
  marginBottom: '2px',
  color: '#FFFFFF',
  fontFamily: 'Lato',
  fontSize: '14px',
  fontStyle: 'italic',
  fontWeight: '700',
  lineHeight: '17px',
  letterSpacing: '0.01em',
  textAlign: 'left',
  textOverflow: 'ellipsis',
  maxWidth: '140px',
  maxHeight: '30px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
};

// eslint-disable-next-line react-refresh/only-export-components -- shared style constants
export const levelStyles = {
  position: 'absolute',
  left: '6px',
  bottom: '6px',
  borderRadius: '4px',
  padding: '0px 8px',

  color: '#FFFFFF',
  fontFamily: 'Lato',
  fontSize: '12px',
  fontWeight: '700',
  lineHeight: '24px',
  letterSpacing: '1px',
  textAlign: 'left',
};

const selectStyles = {
  position: 'absolute',
  right: '4px',
  top: '4px',
  width: '24px',
  height: '24px',
  backgroundColor: '#CED0D4',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    // background: "rgb(202,124,98,.25)",
    // borderRadius: '5px',
    cursor: 'pointer',
    // transition: '0.3s',
    // boxShadow: '0px 0px 5px 5px rgb(202,124,98,.25)',
  },
};

interface RepertoireDetails {
  Name: string;
  Composer: string;
  Level: number;
  Genre: string;
  // Duration: string;
  BPM: number;
}

// At/below this width the anchored popper becomes a centered, blurred-backdrop
// modal. Set wide enough (1100px) to cover phones and tablets in both
// orientations, where the 350px-wide anchored popup is cramped.
const MOBILE_MODAL_QUERY = '(max-width:1100px)';

interface SongDetailsProps {
  repertoireDetails: RepertoireDetails;
  setShowDetails: (show: boolean) => void;
  songDetailsRef: React.RefObject<HTMLDivElement | null>;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  // The song-card hamburger button the popup anchors to. Null until the card
  // has committed to the DOM; the popper stays closed while it is.
  anchorEl: HTMLElement | null;
}

const SongDetails = (props: SongDetailsProps) => {
  const { repertoireDetails, setShowDetails, songDetailsRef, containerRef, anchorEl } = props;
  const isMobile = useMediaQuery(MOBILE_MODAL_QUERY);
  // The arrow element is registered with Popper's `arrow` modifier so Popper
  // slides it along the popup edge to keep it pointing at the anchor.
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);

  // Destroy the popup once its anchor (the song card) scrolls out of the
  // scrollable browse area, so a detached popup never lingers on screen. Only
  // the anchored desktop popper needs this — the mobile modal locks page
  // scrolling, so the card can't leave the view while it's open.
  useEffect(() => {
    if (isMobile || !anchorEl) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setShowDetails(false);
        }
      },
      { root: containerRef?.current ?? null, threshold: 0 },
    );
    observer.observe(anchorEl);
    return () => observer.disconnect();
  }, [isMobile, anchorEl, containerRef, setShowDetails]);

  // Clamp the popup to the scrollable browse container rather than the raw
  // viewport. That container starts just below the page header, so using it as
  // the boundary keeps an upward-shifted popup from sliding behind the header —
  // "available space" = main page container minus the header.
  const boundary = containerRef?.current ?? undefined;

  // Memoized so unrelated re-renders don't re-allocate the array and force
  // Popper to recompute its position (which reads as a tiny jump).
  // - offset: gap between the card and the popup.
  // - flip: swap to the other side when the preferred side has no room.
  // - preventOverflow (altAxis): shift the popup along the cross axis so it
  //   never leaves the boundary box, top/bottom/left/right.
  // - arrow: let Popper position the chevron along the popup's edge.
  const modifiers = useMemo(
    () => [
      { name: 'offset', options: { offset: [0, 12] } },
      { name: 'flip', options: { padding: 8, boundary } },
      { name: 'preventOverflow', options: { altAxis: true, padding: 8, boundary } },
      { name: 'arrow', enabled: Boolean(arrowEl), options: { element: arrowEl, padding: 14 } },
    ],
    [arrowEl, boundary],
  );

  const content = (
    <SongDetailsContent
      repertoireDetails={repertoireDetails}
      setShowDetails={setShowDetails}
      isModal={isMobile}
    />
  );

  // Mobile: a centered modal over a blurred backdrop, not anchored to the card.
  if (isMobile) {
    return (
      <Modal
        open
        onClose={() => setShowDetails(false)}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.55)', backdropFilter: 'blur(6px)' },
          },
        }}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          ref={songDetailsRef}
          sx={{ ...songDetailsSurfaceStyles, width: 'min(90vw, 360px)', outline: 'none' }}
        >
          {content}
        </Box>
      </Modal>
    );
  }

  // Desktop: anchored popup that stays glued to the card, flips/shifts to stay
  // on screen, and carries a chevron that tracks the anchor along its edge.
  return (
    <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} placement="right" modifiers={modifiers}>
      {({ placement }) => {
        const pointsLeft = placement.startsWith('right');
        return (
          <Box
            ref={songDetailsRef}
            sx={{ ...songDetailsSurfaceStyles, position: 'relative', minWidth: '350px' }}
          >
            <Box
              ref={setArrowEl}
              sx={{
                position: 'absolute',
                width: '14px',
                height: '14px',
                // Popper sets `top`; we pin it to the side facing the card.
                ...(pointsLeft ? { left: '-7px' } : { right: '-7px' }),
                '&::before': {
                  content: '""',
                  display: 'block',
                  width: '14px',
                  height: '14px',
                  backgroundColor: '#050B13', // match the popup so it reads as a pointer
                  transform: 'rotate(45deg)',
                },
              }}
            />
            {content}
          </Box>
        );
      }}
    </Popper>
  );
};

interface SongDetailsContentProps {
  repertoireDetails: RepertoireDetails;
  setShowDetails: (show: boolean) => void;
  // In the fullscreen mobile modal the close control is an X in the top-right;
  // the anchored desktop popup keeps the back arrow in the top-left.
  isModal?: boolean;
}

const SongDetailsContent = ({
  repertoireDetails,
  setShowDetails,
  isModal = false,
}: SongDetailsContentProps) => (
  <Box sx={detailsTitleTextStyles}>
    <Box sx={{ textAlign: 'center', marginTop: '6px', marginBottom: '16px', position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          height: '100%',
          top: '0px',
          ...(isModal ? { right: '8px' } : { left: '8px' }),
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={(e) => {
          e.stopPropagation();
          setShowDetails(false);
        }}
      >
        {isModal ? (
          <CloseIcon sx={{ fontSize: '20px', color: '#FFFFFF' }} />
        ) : (
          <img style={{ width: '16px', margin: 'auto' }} src={RepertoireLeftArrow} />
        )}
      </Box>
      SONG DETAILS
    </Box>
    <Box sx={detailsTextStyles}>
      {Object.keys(repertoireDetails).map((k: string) => {
        const key = k as keyof RepertoireDetails;
        return (
          <Box
            key={k}
            sx={{
              display: 'flex',
              borderBottom: '1px solid #31353F',
              padding: '20px 14px',
              margin: '2px 0px',
            }}
          >
            <Box sx={{ width: '35%' }}>{k}</Box>
            <Box sx={{ width: '65%', color: '#ADB1B7' }}>{repertoireDetails[key]}</Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);

interface CompleteToolTipProps {
  songDetailsRef?: React.RefObject<HTMLImageElement | null>;
  pos: { x: number; y: number };
}

const CompleteToolTip = (props: CompleteToolTipProps) => {
  const { songDetailsRef, pos } = props;

  return (
    <Box
      sx={{
        background: '#050B13',
        height: '35px',
        width: '260px',
        borderRadius: '5px',
        // border: '1px solid #767C8C',
        marginLeft: '3px',
        marginBottom: '30px',
        display: 'flex',
        alignContent: 'start',
        padding: '5px',
        paddingLeft: '10px',
        '& p': {
          textAlign: 'left',
          marginLeft: '5px',
          fontSize: '10pt',
          padding: 0,
          margin: 0,
          color: '#ADB1B7',
          fontWeight: '500',
        },
        position: 'fixed',
        left: `${pos.x - 128}px`,
        top: `${pos.y - 70}px`,
        // ...modalPosition,
      }}
      ref={songDetailsRef}
      onClick={() => {}}
    >
      <p>Completion is a function of tempo, number of notes played, and accuracy.</p>
    </Box>
  );
};

// Shared visual surface for the song-details popup, used by both the anchored
// desktop popper and the centered mobile modal.
const songDetailsSurfaceStyles = {
  borderRadius: '8px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)',
  padding: '16px',
  backgroundColor: '#050B13',
  zIndex: 1300,
};

const detailsTitleTextStyles = {
  color: '#FFFFFF',
  fontFamily: 'Lato',
  fontSize: '15px',
  fontHeight: '700',
  lineHeight: '24px',
  letterSpacing: '1px',
  textAlign: 'left',
};

const detailsTextStyles = {
  display: 'flex',
  flexDirection: 'column',
  color: '#ECECEC',
  position: 'relative',
  fontFamily: 'Lato',
  fontSize: '14px',
  fontHeight: '400',
  lineHeight: '16.8px',
  letterSpacing: '1px',
  textAlign: 'left',
};

const _detailsContainerStyles = {};
